import api from "@/services/axios";
import { silentErrors } from "@/services/axios";
import { useQuery } from "@tanstack/react-query";
import type {
  MerchantReviewListParams,
  ModerateReviewCommand,
  SaveReviewReplyCommand,
  ChangeReviewVisibilityCommand,
  ReviewInvitationPreviewRequest,
} from "@/models/club";

export const reviewKeys = {
  all: ["salon-reviews"] as const,
  capabilities: ["salon-reviews", "capabilities"] as const,
};
// Every review screen shows its own error inline, so the global error toast is skipped.
export const reviews = {
  capabilities: async () => (await api.reviewInvitationCapabilitiesList(silentErrors)).data,
  list: async (query: MerchantReviewListParams) =>
    (await api.merchantReviewList(query, silentErrors)).data,
  detail: async (reviewId: number) =>
    (await api.merchantReviewDetail({ reviewId }, silentErrors)).data,
  moderate: async (reviewId: number, data: ModerateReviewCommand) =>
    (await api.merchantReviewModerationCreate({ reviewId }, data, silentErrors)).data,
  reply: async (reviewId: number, data: SaveReviewReplyCommand) =>
    (await api.merchantReviewReplyUpdate({ reviewId }, data, silentErrors)).data,
  visibility: async (reviewId: number, data: ChangeReviewVisibilityCommand) =>
    (await api.merchantReviewVisibilityCreate({ reviewId }, data, silentErrors)).data,
  preview: async (data: ReviewInvitationPreviewRequest) =>
    (await api.reviewInvitationPreviewCreate(data, silentErrors)).data,
  invitation: async (purchaseId: number) =>
    (await api.customerPurchaseReviewInvitationList({ purchaseId }, silentErrors)).data,
  retry: async (invitationId: string, reason: string, key: string) =>
    (
      await api.reviewInvitationRetryCreate(
        { invitationId },
        { reason },
        { ...silentErrors, headers: { "Idempotency-Key": key } },
      )
    ).data,
  // A 404 REQUEST_NOT_FOUND here is the normal "never registered" answer during recovery.
  request: async (requestId: string) =>
    (await api.reviewInvitationRequestsDetail({ requestId }, silentErrors)).data,
};
export function useReviewCapabilities(enabled = true) {
  return useQuery({
    queryKey: reviewKeys.capabilities,
    queryFn: reviews.capabilities,
    enabled,
    retry: false,
    // Read by the navbar, home, invoice form, status cards and details; one request per minute is enough.
    staleTime: 60_000,
  });
}
